import { useMemo, useState } from 'react';
import { Plus, X, Sparkles, Loader2, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Curates the skills a candidate is asked to tick on the public job page.
 *
 * The AI already extracts skills from every job description, but that list runs
 * to 25+ entries and mixes in things like "Communication skills" — nobody
 * unticks those, so as a checkbox they carry no signal. Suggestions are offered
 * one click away and the admin keeps the handful that actually discriminate.
 */

const MAX_SKILLS = 20;

export default function ScreeningSkillsEditor({ value = [], onChange, suggestions = [], parsing = false }) {
    const [draft, setDraft] = useState('');

    const chosen = useMemo(
        () => new Set(value.map(s => s.name.toLowerCase())),
        [value],
    );

    // Only offer what has not already been picked.
    const available = useMemo(
        () => suggestions.filter(s => s && !chosen.has(s.toLowerCase())).slice(0, 14),
        [suggestions, chosen],
    );

    const add = (name, required = false) => {
        const clean = String(name || '').trim();
        if (!clean || clean.length > 60) return;
        if (chosen.has(clean.toLowerCase())) return;
        if (value.length >= MAX_SKILLS) return;
        onChange([...value, { name: clean, required }]);
        setDraft('');
    };

    const remove = (name) => onChange(value.filter(s => s.name !== name));

    const toggleRequired = (name) =>
        onChange(value.map(s => s.name === name ? { ...s, required: !s.required } : s));

    const requiredCount = value.filter(s => s.required).length;

    return (
        <div className="space-y-2">
            <div>
                <Label>
                    Screening skills{' '}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                    Candidates tick these when applying, so you can see who fits before
                    spending an AI evaluation. Keep it to the handful that genuinely
                    separate a good applicant from a poor one.
                </p>
            </div>

            {/* ── Chosen skills ─────────────────────────────────────────── */}
            {value.length > 0 && (
                <div className="space-y-1.5 rounded-lg border border-border bg-muted/20 p-2">
                    {value.map(s => (
                        <div key={s.name} className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
                                {s.name}
                            </span>
                            <button
                                type="button"
                                onClick={() => toggleRequired(s.name)}
                                title={s.required ? 'Marked required — click to make preferred' : 'Marked preferred — click to make required'}
                                className={cn(
                                    'shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors',
                                    s.required
                                        ? 'border-primary/30 bg-primary/10 text-primary'
                                        : 'border-border bg-background text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {s.required ? 'Required' : 'Preferred'}
                            </button>
                            <button
                                type="button"
                                onClick={() => remove(s.name)}
                                aria-label={`Remove ${s.name}`}
                                className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-destructive"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    ))}
                    <p className="border-t border-border/60 pt-1.5 text-[10px] text-muted-foreground">
                        {value.length} skill{value.length === 1 ? '' : 's'} · {requiredCount} required
                    </p>
                </div>
            )}

            {/* ── Add your own ──────────────────────────────────────────── */}
            <div className="flex gap-2">
                <Input
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(draft); } }}
                    placeholder="Add a skill…"
                    maxLength={60}
                    disabled={value.length >= MAX_SKILLS}
                    className="h-8 text-sm"
                />
                <Button type="button" variant="outline" size="sm" className="h-8 shrink-0 gap-1"
                    onClick={() => add(draft)} disabled={!draft.trim() || value.length >= MAX_SKILLS}>
                    <Plus className="h-3.5 w-3.5" />Add
                </Button>
            </div>

            {/* ── Suggestions from the job description ──────────────────── */}
            {parsing ? (
                <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Reading the job description for skill suggestions…
                </p>
            ) : available.length > 0 ? (
                <div className="space-y-1.5">
                    <p className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                        <Sparkles className="h-3 w-3 text-violet-500" />
                        From your job description — click to add
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {available.map(s => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => add(s, true)}
                                disabled={value.length >= MAX_SKILLS}
                                className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground disabled:opacity-50"
                            >
                                + {s}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            {value.length >= MAX_SKILLS && (
                <p className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                    <Info className="h-3 w-3" />
                    {MAX_SKILLS} is the maximum — a longer checklist gets skimmed rather than read.
                </p>
            )}
        </div>
    );
}
